/**
 * Keyboard shortcuts.
 *
 * Shared actions (play/pause, seek) are permission-checked by the same command
 * path as the buttons; local actions (mute, fullscreen, captions, help) never
 * touch the room. Shortcuts are ignored while the user is typing in an
 * editable field, so typing "f" into the URL or username box cannot toggle
 * fullscreen.
 */

export type ShortcutAction =
  /** Shared: play or pause the room. */
  | 'toggle-play'
  /** Shared: seek relative to the room's current position. */
  | 'seek-back'
  | 'seek-forward'
  /** Local only. */
  | 'toggle-mute'
  | 'toggle-fullscreen'
  | 'toggle-captions'
  | 'toggle-help';

/** Relative seek step for the arrow shortcuts. */
export const SEEK_STEP_MS = 5000;

export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

const ACTION_BY_KEY: Record<string, ShortcutAction> = {
  k: 'toggle-play',
  ' ': 'toggle-play',
  ArrowLeft: 'seek-back',
  j: 'seek-back',
  ArrowRight: 'seek-forward',
  l: 'seek-forward',
  m: 'toggle-mute',
  f: 'toggle-fullscreen',
  c: 'toggle-captions',
  '?': 'toggle-help',
};

/**
 * The action for a key event, or `null` when the event is not a shortcut.
 * Modifier combinations are left to the browser.
 */
export function shortcutFor(event: KeyboardEvent): ShortcutAction | null {
  if (event.ctrlKey || event.metaKey || event.altKey) return null;
  if (isEditableTarget(event.target)) return null;
  const key = event.key.length === 1 ? event.key.toLowerCase() : event.key;
  return ACTION_BY_KEY[key] ?? null;
}

/** Documented shortcut list, rendered in the help panel and the user guide. */
export const SHORTCUT_HELP: readonly {
  readonly keys: string;
  readonly what: string;
  readonly scope: 'room' | 'local';
}[] = [
  { keys: 'Space or K', what: 'Play / pause for everyone', scope: 'room' },
  { keys: '← / J', what: 'Seek back 5 seconds for everyone', scope: 'room' },
  { keys: '→ / L', what: 'Seek forward 5 seconds for everyone', scope: 'room' },
  { keys: 'M', what: 'Mute / unmute (only you)', scope: 'local' },
  { keys: 'F', what: 'Fullscreen (only you)', scope: 'local' },
  { keys: 'C', what: 'Captions on / off (only you)', scope: 'local' },
  { keys: '?', what: 'Show or hide this help', scope: 'local' },
];
